import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsPeople, BsShieldCheck, BsEye } from 'react-icons/bs';
import { useLeagues } from '../../hooks/useLeagues';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ErrorMessage } from '../../components/ui/ErrorMessage';

interface LeaguesListProps {
  // Props adicionales si necesitas
}

export const LeaguesList: React.FC<LeaguesListProps> = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const { leagues, loading, error, refetch } = useLeagues();

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Activa' : 'Inactiva'}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No definida';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleLeagueClick = (leagueId: number) => {
    navigate(`/leagues/${leagueId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorMessage message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ligas</h2>
          <p className="text-gray-600">Lista de todas las ligas disponibles</p>
        </div>
      </div>

      {leagues.length === 0 ? (
        <div className="text-center py-12">
          <BsShieldCheck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay ligas</h3>
          <p className="mt-1 text-sm text-gray-500">
            No se encontraron ligas disponibles.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {leagues.map((league) => (
            <div
              key={league.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 cursor-pointer"
              onClick={() => handleLeagueClick(league.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {league.name}
                    </h3>
                    <div className="mb-3">
                      {getStatusBadge(league.is_active)}
                    </div>
                  </div>
                  <BsEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </div>

                <div className="space-y-3">
                  {league.description && (
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {league.description}
                    </p>
                  )}

                  <div className="space-y-2">
                    {league.start_date && (
                      <div className="flex items-center text-sm text-gray-600">
                        📅 <span className="ml-2">Inicio: {formatDate(league.start_date)}</span>
                      </div>
                    )}

                    {league.end_date && (
                      <div className="flex items-center text-sm text-gray-600">
                        📅 <span className="ml-2">Fin: {formatDate(league.end_date)}</span>
                      </div>
                    )}

                    <div className="flex items-center text-sm text-gray-600">
                      <BsPeople className="h-4 w-4 mr-2" />
                      <span>Ver ranking de jugadores</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      ID: {league.id}
                    </span>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
                      Ver ranking
                      <BsEye className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginación simple */}
      {leagues.length > 0 && (
        <div className="flex justify-center items-center space-x-4 mt-8">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-700">
            Página {currentPage}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};
